import {
  Component,
  Host,
  Prop,
  Element,
  State,
  getAssetPath,
  Method,
  h,
  Watch
} from "@stencil/core";

import { MercuryBundles, getIconPath } from "@genexus/mercury";

import { config } from "../../common/config";
import { Locale } from "../../common/locale";
import { getSelectedItem, setSelectedItem } from "../../common/common";
import {
  mapOptionsToComboBoxItemModel,
  renderChCheckboxItems
} from "../../common/helpers";
import { FormSubmitResult, GxOption } from "../../common/types";
import {
  ControlValidation,
  validateControls
} from "../../common/form-validation";
import {
  TabModel,
  ComboBoxModel,
  ChCheckboxCustomEvent
} from "@genexus/chameleon-controls-library";
import { removeIndex } from "../../common/array";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/tab",
  "components/tooltip",
  "utils/form--full",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar"
];

const WINDOWS_AUTHENTICATION_VALUE: string = "WINDOWS_AUTHENTICATION";
const LOCALDB_INSTANCE: string = "(LocalDB)\\MSSQLLocalDB";
const MAIN_SECTION_CLASSES: string = "main";
const BASIC_SECTION_PARENT_CLASSES: string = "basic";

const SYSTEM_ICON = getIconPath({
  category: "system",
  name: "folder",
  colorType: "primary"
});

const WARNING_ICON = getIconPath({
  category: "system",
  name: "warning",
  colorType: "on-interactive"
});

const DATABASE_ICON = getIconPath({
  category: "system",
  name: "database",
  colorType: "primary"
});

const SUCCESS_ICON = getIconPath({
  category: "system",
  name: "check-circle",
  colorType: "success"
});

const ERROR_ICON = getIconPath({
  category: "system",
  name: "warning",
  colorType: "error"
});

const DATABASE_NAME_PREFIX: string = "GX_KB_";

const BASIC_TAB_SELECTOR: string = "basic";
const ADVANCED_TAB_SELECTOR: string = "advanced";

@Component({
  tag: "gx-ide-new-kb",
  styleUrl: "new-kb.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/new-kb"]
})
export class GxIdeNewKb {
  #advancedTabHasBeenRendered: boolean = false;
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;
  /**
   * frontEndsState saves the id's of the front ends checkboxes that are checked
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #frontEndsCheckedMap = new Map<string, string[]>();
  #selectedPrototypingEnvironmentId: string;
  #selectedDataSourceId: string;
  #shortcutsSrc = getAssetPath(`./gx-ide-assets/new-kb/shortcuts.json`);
  #kbSectionsModel: TabModel = [];

  @Element() el: HTMLGxIdeNewKbElement;

  /* References needed to collect data con "Create" button submit*/
  #authenticationTypeEl!: HTMLChComboBoxRenderElement;
  #collationEl!: HTMLChComboBoxRenderElement;
  #createDataFilesInKBFolderEl!: HTMLChCheckboxElement;
  #databaseNameEl!: HTMLChEditElement;
  #dataSourceEl!: HTMLChComboBoxRenderElement;
  #kbNameEl!: HTMLChEditElement;
  #locationEl!: HTMLChEditElement;
  #locationElRegistered = false;
  #credentialControlsRegistered = false;
  #passwordEl!: HTMLChEditElement;
  #prototypingEnvironmentEl!: HTMLChComboBoxRenderElement;
  #prototypingTargetEl!: HTMLChComboBoxRenderElement;
  #savePasswordEl!: HTMLChCheckboxElement;
  #serverNameEl!: HTMLChComboBoxRenderElement;
  #userInterfaceLanguageEl!: HTMLChComboBoxRenderElement;
  #userNameEl!: HTMLChEditElement;

  /**
   * Monotonic id of the latest collations request. Used to discard stale
   * responses: a slow request for an invalid instance can resolve after a
   * newer request for a valid instance, and without this guard it would
   * overwrite the UI with the wrong (failed) result.
   */
  #collationsRequestId = 0;

  #chShortcutsEl: HTMLChShortcutsElement;

  @State() advancedTabIsVisible: boolean = false;
  @State() loadingAdvancedTab: boolean = false;
  @State() browseForMoreDialogOpen: boolean = false;
  @State() databaseNameValue: string = "";
  /**
   * Allows selecting multiple generators for the front end
   * TODO: Check if this property can be deleted, since we can call getFrontEndsCallback.
   */
  @State() frontEnds: GxOption[];
  /**
   * True when front ends are available but none is selected, meaning the KB
   * will be generated as backend only. Drives the backend-only callout.
   */
  @State() noFrontEndSelected: boolean = false;
  @State() validatableControls = new Map<string, ControlValidation>();
  @State() connectionStatus: "connecting" | "success" | "failed" | undefined;
  @State() userNameValue: string = "";
  @State() passwordValue: string = "";

  // representations of GxOption[] as ComboBoxModel, and initialValue for each one.
  @State() authenticationTypes: GxOption[] = [];
  @Watch("authenticationTypes")
  watchAuthenticationTypes(newValue: GxOption[]) {
    if (newValue?.length) {
      this.authenticationTypesComboBoxModel =
        mapOptionsToComboBoxItemModel(newValue);
      this.authenticationTypeValue = getSelectedItem(newValue);
    }
  }
  @State() authenticationTypesComboBoxModel: ComboBoxModel = [];
  @State() authenticationTypeValue: string;
  @State() collations: GxOption[] = [];
  @Watch("collations")
  watchCollations(newValue: GxOption[]) {
    if (!newValue?.length) {
      this.collationsComboBoxModel = [];
      return;
    }
    this.collationsComboBoxModel = mapOptionsToComboBoxItemModel(newValue);
  }
  @State() collationsComboBoxModel: ComboBoxModel = [];
  @State() defaultCollation: string;
  @State() defaultDataSourcesComboBoxModel: ComboBoxModel = [];
  @State() prototypingTargetsComboBoxModel: ComboBoxModel = [];
  @State() prototypingTargetsInitialValue: string;
  @State() prototypingEnvironmentsComboBoxModel: ComboBoxModel = [];
  @State() prototypingEnvironmentsInitialValue: string;
  @State() serverInstances: GxOption[] = [];
  @Watch("serverInstances")
  watchServerInstances(newValue: GxOption[]) {
    if (!newValue?.length) {
      this.serverInstancesComboBoxModel = [];
      return;
    }
    this.serverInstancesComboBoxModel = mapOptionsToComboBoxItemModel(newValue);
  }
  @State() serverInstancesComboBoxModel: ComboBoxModel = [];
  @State() serverNameValue: string = "";
  @State() userInterfaceLanguagesComboBoxModel: ComboBoxModel = [];
  @State() userInterfaceLanguagesInitialValue: string;
  @State() selectedTab: string = BASIC_TAB_SELECTOR;

  /**
   * Callback invoked when the user wants to cancel KB creation (‘Cancel’ button)
   */
  @Prop() readonly cancelCallback: CancelCallback;

  /**
   * Specifies whether the location field (and its folder-picker button) is
   * shown in the header, allowing the user to override the default `kbLocation`
   * path. Independent from `isAdvanced`.
   */
  @Prop() readonly canSetLocation: boolean = false;

  /**
   * Callback invoked when the advanced tab is selected for the first time.
   * Returns the data needed to populate the advanced tab fields lazily,
   * avoiding unnecessary loading at component initialization.
   */
  @Prop() readonly getAdvancedTabDataCallback: GetAdvancedTabDataCallback;

  /**
   * Callback that will be invoked when the user confirms KB creation (‘Create’ button)
   */
  @Prop() readonly createCallback!: CreateCallback;

  /**
   * It allows defining whether the option to create the data files in the KB folder will be shown in the advanced tab
   */
  @Prop({ mutable: true }) createDataFilesInKBFolder: boolean = true;

  /**
   * Name of the database where we are going to persist the information of our KB
   */
  @Prop() readonly databaseName: string;

  /**
   * It allows defining the DBMS that will be used in the solution
   */
  @Prop({ mutable: true }) defaultDataSources: GxOption[] = [];
  @Watch("defaultDataSources")
  watchDefaultDataSources(newValue: GxOption[]) {
    if (newValue?.length) {
      this.defaultDataSourcesComboBoxModel =
        mapOptionsToComboBoxItemModel(newValue);
      const result = setSelectedItem(newValue, this.#selectedDataSourceId);
      this.#selectedDataSourceId = getSelectedItem(result);
    }
  }

  /**
   * Callback invoked when the user wants to cancel KB creation (‘Cancel’ button)
   */
  @Prop() readonly getDataSourcesCallback: GetDataSourcesCallback;

  /**
   * Callback invoked when the user changes the prototyping environment, allowing for the modification of the list of front ends
   */
  @Prop() readonly getFrontEndsCallback!: GetFrontEndsCallback;

  /**
   * Callback invoked when the user changes the prototyping target id, allowing for the modification of the list of prototyping environments
   */
  @Prop()
  readonly getPrototypingEnvironmentsCallback!: GetPrototypingEnvironmentsCallback;

  /**
   * Specify whether it is feasible to display information related to local configuration parameters
   */
  @Prop() readonly isAdvanced = false;

  /**
   * Default suggested path to a directory where the information related to the KB will be stored/generated
   */
  @Prop({ mutable: true }) kbLocation: string;

  /**
   * The knowledge base default suggested name
   */
  @Prop() readonly kbName: string;

  /**
   * Password for the database connection. Visible if the Authentication Type is different from Windows Authentication (first item of the combo)
   */
  @Prop() readonly password: string;

  /**
   * It allows selecting the default environment that the KB will have (additional environments can be created later)
   */
  @Prop({ mutable: true }) defaultPrototypingEnvironments: GxOption[] = [];
  @Watch("defaultPrototypingEnvironments")
  watchDefaultPrototypingEnvironments(newValue: GxOption[]) {
    if (newValue?.length) {
      this.prototypingEnvironmentsComboBoxModel =
        mapOptionsToComboBoxItemModel(newValue);
      this.#selectedPrototypingEnvironmentId = getSelectedItem(newValue);
      this.#updateFrontEnds();
    }
  }

  /**
   * Path to a directory where the information related to the KB will be stored/generated
   */
  @Prop() readonly prototypingTargets: GxOption[] = [];
  @Watch("prototypingTargets")
  watchPrototypingTargets(newValue: GxOption[]) {
    if (newValue?.length) {
      this.prototypingTargetsComboBoxModel =
        mapOptionsToComboBoxItemModel(newValue);
      this.prototypingTargetsInitialValue = getSelectedItem(newValue);
      this.#getPrototypingEnvironments();
    }
  }

  /**
   * Visible if something other than Windows Authentication is selected
   */
  @Prop() readonly savePassword: boolean = false;

  /**
   * This is a function provided by the developer that returns a string, with the location path
   */
  @Prop() readonly selectLocationCallback!: SelectLocationCallback;

  /**
   * Callback invoked when the user clicks the "Browse for more..." button next
   * to the Server Name combo. The host is expected to display its own server
   * picker UI and resolve with the name of the selected server instance, which
   * will be set as the value of the Server Name combo.
   */
  @Prop() readonly browseForMoreCallback: BrowseForMoreCallback;

  /**
   * Callback invoked when the user selects a different server instance,
   * allowing the host to fetch the collations available on that instance.
   */
  @Prop()
  readonly getCollationsForInstanceCallback: GetCollationsForInstanceCallback;

  /**
   * Specify the language in which the application screens will be developed (default language)
   */
  @Prop() readonly userInterfaceLanguages: GxOption[] = [];
  @Watch("userInterfaceLanguages")
  watchUserInterfaceLanguages(newValue: GxOption[]) {
    if (newValue?.length) {
      this.userInterfaceLanguagesComboBoxModel =
        mapOptionsToComboBoxItemModel(newValue);
      this.userInterfaceLanguagesInitialValue = getSelectedItem(newValue);
    }
  }

  /**
   * Username for the database connection. Visible if the Authentication Type is different from Windows Authentication
   */
  @Prop({ mutable: true }) userName: string;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.databaseNameValue = this.#computeDefaultDatabaseName(this.kbName);
    if (this.databaseName) {
      this.databaseNameValue = this.databaseName;
    }
    this.userNameValue = this.userName ?? "";
    this.passwordValue = this.password ?? "";
    this.#kbSectionsModel = [
      {
        id: BASIC_TAB_SELECTOR,
        name: this.#componentLocale.tabBasic.tabLabel
      },
      {
        id: ADVANCED_TAB_SELECTOR,
        name: this.#componentLocale.tabAdvanced.tabLabel
      }
    ];
  }

  connectedCallback() {
    this.watchDefaultDataSources(this.defaultDataSources);
    this.watchDefaultPrototypingEnvironments(
      this.defaultPrototypingEnvironments
    );
    this.watchPrototypingTargets(this.prototypingTargets);
    this.watchUserInterfaceLanguages(this.userInterfaceLanguages);
  }

  componentDidLoad() {
    this.#initializeValidatableControlsBasicTab();
  }

  componentDidRender() {
    // Defer the one-time setup until the real advanced content is rendered
    // (not the loader); otherwise the control refs it reads are still undefined.
    if (
      this.advancedTabIsVisible &&
      !this.loadingAdvancedTab &&
      !this.#advancedTabHasBeenRendered
    ) {
      // just call this once.
      this.#initialSetupAdvancedTab();
      this.#initializeValidatableControlsAdvancedTab();
      this.#advancedTabHasBeenRendered = true;
    }

    // Register the location input for validation once it's rendered
    if (!this.#locationElRegistered && this.#locationEl) {
      this.validatableControls.set(this.#locationEl.id, {
        reference: this.#locationEl,
        hasError: false,
        message: undefined
      });
      this.#locationElRegistered = true;
    }

    // Register credential controls when they become visible (SQL Auth selected)
    if (
      this.authenticationTypeValue !== WINDOWS_AUTHENTICATION_VALUE &&
      !this.#credentialControlsRegistered &&
      this.#userNameEl &&
      this.#passwordEl
    ) {
      this.validatableControls.set(this.#userNameEl.id, {
        reference: this.#userNameEl,
        hasError: false,
        message: undefined
      });
      this.validatableControls.set(this.#passwordEl.id, {
        reference: this.#passwordEl,
        hasError: false,
        message: undefined
      });
      this.#credentialControlsRegistered = true;
    }

    // Reset flag when switching back to Windows Auth so they re-register next time
    if (
      this.authenticationTypeValue === WINDOWS_AUTHENTICATION_VALUE &&
      this.#credentialControlsRegistered
    ) {
      this.#credentialControlsRegistered = false;
    }
  }

  /**
   * Suspends or reactivates the shortcuts
   */
  @Method()
  async suspendShortcuts(suspendShortcuts: boolean) {
    if (suspendShortcuts) {
      this.#chShortcutsEl.suspend = true;
    } else {
      this.#chShortcutsEl.suspend = false;
    }
  }

  #computeDefaultDatabaseName = (kbName: string): string =>
    `${DATABASE_NAME_PREFIX}${kbName ?? ""}`;

  #kbNameInputHandler = (e: CustomEvent<string> | InputEvent) => {
    const kbNameValue = (e.detail ?? this.#kbNameEl?.value) as string;
    this.databaseNameValue = this.#computeDefaultDatabaseName(kbNameValue);
  };

  #databaseNameInputHandler = (e: CustomEvent<string> | InputEvent) => {
    this.databaseNameValue = (e.detail ??
      this.#databaseNameEl?.value) as string;
  };

  #handlerAuthenticationTypeInput = async (
    e: CustomEvent<string> | InputEvent
  ) => {
    this.authenticationTypeValue = (e as CustomEvent<string>)?.detail as string;
    this.collations = [];
    this.defaultCollation = undefined;
    this.connectionStatus = undefined;
    await this.#loadCollationsForCurrentConnection();
  };

  #handlerCredentialsChanged = async (): Promise<void> => {
    this.userNameValue = this.#userNameEl?.value ?? "";
    this.passwordValue = this.#passwordEl?.value ?? "";
    await this.#loadCollationsForCurrentConnection();
  };

  #browseForMoreHandler = async (): Promise<void> => {
    if (!this.browseForMoreCallback) {
      return;
    }
    this.browseForMoreDialogOpen = true;
    try {
      const selectedInstance = await this.browseForMoreCallback();
      if (selectedInstance) {
        this.serverNameValue = selectedInstance;
        if (this.#serverNameEl) {
          this.#serverNameEl.value = selectedInstance;
        }
        await this.#serverNameChangedHandler(
          new CustomEvent<string>("change", { detail: selectedInstance })
        );
      }
    } catch (error) {
    } finally {
      this.browseForMoreDialogOpen = false;
    }
  };

  #serverNameChangedHandler = async (
    e: CustomEvent<string> | InputEvent
  ): Promise<void> => {
    const selectedInstance =
      (e as CustomEvent<string>)?.detail ??
      (e.target as HTMLInputElement)?.value;
    this.serverNameValue = selectedInstance ?? "";
    this.#applyCreateDataFilesInKBFolderForServer();
    await this.#loadCollationsForCurrentConnection();
  };

  #classifyServer = (
    serverName: string
  ): "localdb" | "local" | "remote" | "empty" => {
    if (!serverName) {
      return "empty";
    }
    if (serverName.toLowerCase() === LOCALDB_INSTANCE.toLowerCase()) {
      return "localdb";
    }
    const isLocal = (this.serverInstances ?? []).some(
      s => s.label?.toLowerCase() === serverName.toLowerCase()
    );
    return isLocal ? "local" : "remote";
  };

  #createDataFilesInKBFolderDisabled = (): boolean => {
    const kind = this.#classifyServer(this.serverNameValue);
    return kind === "localdb" || kind === "remote" || kind === "empty";
  };

  #sqlAuthMissingCredentials = (): boolean => {
    const authType = this.authenticationTypeValue;
    if (!authType || authType === WINDOWS_AUTHENTICATION_VALUE) {
      return false;
    }
    return !this.userNameValue || !this.passwordValue;
  };

  #applyCreateDataFilesInKBFolderForServer = (): void => {
    const kind = this.#classifyServer(this.serverNameValue);
    if (kind === "local" || kind === "localdb") {
      this.createDataFilesInKBFolder = true;
    } else if (kind === "remote") {
      this.createDataFilesInKBFolder = false;
    }
  };

  #loadCollationsForCurrentConnection = async (): Promise<void> => {
    const selectedInstance = this.serverNameValue;
    if (!selectedInstance || !this.getCollationsForInstanceCallback) {
      return;
    }
    const authType = this.authenticationTypeValue;
    const isWindowsAuth = authType === WINDOWS_AUTHENTICATION_VALUE;
    const userName = isWindowsAuth ? undefined : this.#userNameEl?.value;
    const password = isWindowsAuth ? undefined : this.#passwordEl?.value;

    if (!isWindowsAuth && authType && (!userName || !password)) {
      return;
    }

    // Tag this request so we can ignore its response if a newer request is
    // fired before it resolves (e.g. the user fixes an invalid server name
    // while the slow request for the invalid one is still pending).
    const requestId = ++this.#collationsRequestId;
    this.connectionStatus = "connecting";
    try {
      const result = await this.getCollationsForInstanceCallback(
        selectedInstance,
        authType,
        userName,
        password
      );
      if (requestId !== this.#collationsRequestId) {
        // A newer request superseded this one; discard the stale response.
        return;
      }
      if (result) {
        this.collations = result.collations ?? [];
        this.defaultCollation = result.defaultCollation ?? undefined;
        this.connectionStatus = "success";
      } else {
        this.collations = [];
        this.defaultCollation = undefined;
        this.connectionStatus = "failed";
      }
    } catch {
      if (requestId !== this.#collationsRequestId) {
        return;
      }
      this.collations = [];
      this.defaultCollation = undefined;
      this.connectionStatus = "failed";
    }
  };

  #prototypingEnvironmentValueChangedHandler = (
    event: CustomEvent<string> | InputEvent
  ): void => {
    this.#selectedPrototypingEnvironmentId = event.detail as string;
    this.#getDataSources();
    this.#updateFrontEnds();
  };

  #prototypingTargetValueChangedHandler = async (): Promise<void> => {
    await this.#getPrototypingEnvironments();
    this.#getDataSources();
  };

  #dataSourceValueChangedHandler = () => {
    this.#selectedDataSourceId = this.#dataSourceEl.value;
  };

  #renderAdvanced = (): Element[] => {
    const showCredentials =
      this.authenticationTypeValue !== WINDOWS_AUTHENTICATION_VALUE;

    return [
      <section class="kb-storage-card" part="kb-storage-card">
        <header class="kb-storage-card__header">
          <div class="kb-storage-card__heading">
            <h2 class="kb-storage-card__title body-semi-bold-m">
              {this.#componentLocale.tabAdvanced.tabTitle}
            </h2>
          </div>
        </header>

        <div class="kb-storage-card__grid">
          <div class="kb-storage-column">
            <div class="kb-storage-column__title body-regular-xs">
              <span class="kb-storage-column__title-text">
                {this.#componentLocale.tabAdvanced.connectionGroup}
              </span>
              <span class="kb-storage-column__title-rule" aria-hidden="true" />
            </div>

            <div class="field-group field-group--stacked">
              <div class="field field-block server-name-field">
                <label class="label" htmlFor="serverName">
                  {this.#componentLocale.tabAdvanced.serverName.label}
                </label>
                <div class="server-name-wrapper">
                  <ch-combo-box-render
                    class="combo-box"
                    value={this.serverNameValue}
                    ref={(el: HTMLChComboBoxRenderElement) =>
                      (this.#serverNameEl = el as HTMLChComboBoxRenderElement)
                    }
                    id="serverName"
                    part="server-name"
                    model={this.serverInstancesComboBoxModel}
                    suggest
                    suggestDebounce={config.inputDebounce}
                    onInput={this.#serverNameChangedHandler}
                    onChange={this.#serverNameChangedHandler}
                  ></ch-combo-box-render>
                  <button
                    class="button-tertiary button-icon-only"
                    aria-label={
                      this.#componentLocale.tabAdvanced.serverName.browseForMore
                    }
                    part="button-browse-for-more"
                    onClick={this.#browseForMoreHandler}
                  >
                    <ch-image class="icon-m" src={DATABASE_ICON}></ch-image>
                    {!this.browseForMoreDialogOpen && (
                      <ch-tooltip
                        class="tooltip"
                        actionElement={null}
                        blockAlign="outside-start"
                        inlineAlign="center"
                        delay={config.tooltipSettings.delay}
                      >
                        {
                          this.#componentLocale.tabAdvanced.serverName
                            .browseForMore
                        }
                      </ch-tooltip>
                    )}
                  </button>
                </div>
                {this.#evaluateTooltipRender(this.#serverNameEl)}
              </div>

              <div class="field field-block database-field">
                <label class="label" htmlFor="databaseName">
                  {this.#componentLocale.tabAdvanced.databaseName.label}
                </label>
                <ch-edit
                  class="input"
                  value={this.databaseNameValue}
                  id="databaseName"
                  part="database-name"
                  onInput={this.#databaseNameInputHandler}
                  ref={(el: HTMLChEditElement) =>
                    (this.#databaseNameEl = el as HTMLChEditElement)
                  }
                ></ch-edit>

                {this.#evaluateTooltipRender(this.#databaseNameEl)}
              </div>

              <div class="field field-block collation-field">
                <label class="label" htmlFor="collation">
                  {this.#componentLocale.tabAdvanced.collation.label}
                </label>
                <ch-combo-box-render
                  class="combo-box"
                  disabled={
                    !this.serverNameValue || this.collations.length === 0
                  }
                  placeholder={
                    this.collations.length === 0
                      ? this.#componentLocale.tabAdvanced.collation
                          .disabledPlaceholder
                      : ""
                  }
                  value={this.defaultCollation}
                  ref={(el: HTMLChComboBoxRenderElement) =>
                    (this.#collationEl = el as HTMLChComboBoxRenderElement)
                  }
                  model={this.collationsComboBoxModel}
                  id="collation"
                  part="collation"
                ></ch-combo-box-render>
                {this.#evaluateTooltipRender(this.#collationEl)}
              </div>

              <ch-checkbox
                class="checkbox"
                checkedValue="true"
                value={this.createDataFilesInKBFolder ? "true" : undefined}
                disabled={this.#createDataFilesInKBFolderDisabled()}
                caption={
                  this.#componentLocale.tabAdvanced.createDataFilesInKbFolder
                }
                part="create-data-in-kb"
                id="createDataFilesInKBFolder"
                ref={(el: HTMLChCheckboxElement) =>
                  (this.#createDataFilesInKBFolderEl =
                    el as HTMLChCheckboxElement)
                }
              ></ch-checkbox>
            </div>
          </div>

          <div class="kb-storage-column">
            <div class="kb-storage-column__title body-regular-xs">
              <span class="kb-storage-column__title-text">
                {this.#componentLocale.tabAdvanced.authenticationGroup}
              </span>
              <span class="kb-storage-column__title-rule" aria-hidden="true" />
            </div>

            <div class="field-group field-group--stacked">
              <div class="field field-block authentication-type-field">
                <label class="label" htmlFor="authenticationType">
                  {this.#componentLocale.tabAdvanced.authenticationType.label}
                </label>
                <ch-combo-box-render
                  class="combo-box"
                  disabled={this.authenticationTypes.length === 0}
                  placeholder={
                    this.authenticationTypes.length === 0
                      ? this.#componentLocale.tabAdvanced.authenticationType
                          .disabledPlaceholder
                      : ""
                  }
                  value={this.authenticationTypeValue}
                  ref={(el: HTMLChComboBoxRenderElement) =>
                    (this.#authenticationTypeEl =
                      el as HTMLChComboBoxRenderElement)
                  }
                  model={this.authenticationTypesComboBoxModel}
                  id="authenticationType"
                  part="authentication-type"
                  onInput={
                    this.authenticationTypes.length > 0
                      ? this.#handlerAuthenticationTypeInput
                      : undefined
                  }
                ></ch-combo-box-render>
                {this.#evaluateTooltipRender(this.#authenticationTypeEl)}
              </div>

              {showCredentials ? (
                <div class="kb-storage-credentials">
                  <div class="field field-block username-field">
                    <label class="label" htmlFor="userName">
                      {this.#componentLocale.tabAdvanced.userName.label}
                    </label>
                    <ch-edit
                      class="input"
                      value={this.userName}
                      id="userName"
                      part="user-name"
                      ref={(el: HTMLChEditElement) =>
                        (this.#userNameEl = el as HTMLChEditElement)
                      }
                      onInput={this.#handlerCredentialsChanged}
                      debounce={config.inputDebounce}
                    ></ch-edit>
                    {this.#evaluateTooltipRender(this.#userNameEl)}
                  </div>

                  <div class="field field-block password-field">
                    <label class="label" htmlFor="password">
                      {this.#componentLocale.tabAdvanced.password.label}
                    </label>
                    <ch-edit
                      class="input"
                      type="password"
                      showPasswordButton
                      value={this.password}
                      id="password"
                      part="password"
                      ref={(el: HTMLChEditElement) =>
                        (this.#passwordEl = el as HTMLChEditElement)
                      }
                      onInput={this.#handlerCredentialsChanged}
                      debounce={config.inputDebounce}
                    ></ch-edit>
                    {this.#evaluateTooltipRender(this.#passwordEl)}
                  </div>

                  <ch-checkbox
                    class="checkbox"
                    checkedValue="password"
                    value="password"
                    caption={this.#componentLocale.tabAdvanced.savePassword}
                    part="save-password"
                    id="savePassword"
                    ref={(el: HTMLChCheckboxElement) =>
                      (this.#savePasswordEl = el as HTMLChCheckboxElement)
                    }
                    disabled
                  ></ch-checkbox>
                </div>
              ) : (
                <div class="kb-storage-callout" part="windows-auth-callout">
                  <ch-image
                    class="icon-l"
                    src={WARNING_ICON}
                    type="mask"
                  ></ch-image>

                  <div class="kb-storage-callout-inner">
                    <span class="kb-storage-callout__title body-semi-bold-s">
                      {
                        this.#componentLocale.tabAdvanced
                          .windowsAuthCalloutTitle
                      }
                    </span>
                    <span class="kb-storage-callout__description body-regular-xs">
                      {
                        this.#componentLocale.tabAdvanced
                          .windowsAuthCalloutDescription
                      }
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    ];
  };

  #renderBasic = (): HTMLElement[] => {
    return [
      <section class="basic__header field-group dialog-header-with-border spacing-body-inline">
        <div
          // #prototyping  target
          class="field field-block prototyping-target"
        >
          <label class="label" htmlFor="prototypingTarget">
            {this.#componentLocale.tabBasic.prototypingTarget.label}
          </label>

          <ch-combo-box-render
            id="prototypingTarget"
            class="combo-box"
            part="prototyping-target"
            disabled={this.prototypingTargets.length === 0}
            placeholder={
              this.prototypingTargets.length === 0
                ? this.#componentLocale.tabBasic.prototypingTarget
                    .disabledPlaceholder
                : ""
            }
            value={this.prototypingTargetsInitialValue}
            model={this.prototypingTargetsComboBoxModel}
            onInput={
              this.prototypingTargets.length > 0
                ? this.#prototypingTargetValueChangedHandler
                : undefined
            }
            ref={(el: HTMLChComboBoxRenderElement) =>
              (this.#prototypingTargetEl = el as HTMLChComboBoxRenderElement)
            }
          ></ch-combo-box-render>
          {this.#evaluateTooltipRender(this.#prototypingTargetEl)}
        </div>

        <div
          // #user interface
          class="field field-block user-interface-language"
        >
          <label class="label" htmlFor="userInterfaceLanguage">
            {this.#componentLocale.tabBasic.userInterfaceLanguage.label}
          </label>
          <ch-combo-box-render
            class="combo-box"
            disabled={this.userInterfaceLanguages.length === 0}
            placeholder={
              this.userInterfaceLanguages.length === 0
                ? this.#componentLocale.tabBasic.userInterfaceLanguage
                    .disabledPlaceholder
                : ""
            }
            value={this.userInterfaceLanguagesInitialValue}
            ref={(el: HTMLChComboBoxRenderElement) =>
              (this.#userInterfaceLanguageEl =
                el as HTMLChComboBoxRenderElement)
            }
            model={this.userInterfaceLanguagesComboBoxModel}
            id="userInterfaceLanguage"
            part="user-interface-language"
          ></ch-combo-box-render>
          {this.#evaluateTooltipRender(this.#userInterfaceLanguageEl)}
        </div>
      </section>,

      <section
        class="section__back-end"
        // backend
      >
        <h2
          // back end
          class="dialog-header dialog-header-with-border dialog-header__back-end subtitle-regular-xs	 text-align-center spacing-body-inline"
        >
          {this.#componentLocale.tabBasic.backEnd.backEndLabel}
        </h2>
        <div class="field-group main__back-end spacing-body-inline scrollable">
          <div
            // #prototyping environment
            class="field field-block"
          >
            <label class="label" htmlFor="prototypingEnvironment">
              {
                this.#componentLocale.tabBasic.backEnd.prototypingEnvironment
                  .label
              }
            </label>
            <ch-combo-box-render
              class="combo-box"
              disabled={this.defaultPrototypingEnvironments.length === 0}
              placeholder={
                this.defaultPrototypingEnvironments.length === 0
                  ? this.#componentLocale.tabBasic.backEnd
                      .prototypingEnvironment.disabledPlaceholder
                  : ""
              }
              value={this.#selectedPrototypingEnvironmentId}
              ref={(el: HTMLChComboBoxRenderElement) =>
                (this.#prototypingEnvironmentEl =
                  el as HTMLChComboBoxRenderElement)
              }
              model={this.prototypingEnvironmentsComboBoxModel}
              onInput={this.#prototypingEnvironmentValueChangedHandler}
              part="prototyping-environment"
              id="prototypingEnvironment"
            ></ch-combo-box-render>
            {this.#evaluateTooltipRender(this.#prototypingEnvironmentEl)}
          </div>

          <div
            // #data source
            class="field field-block"
          >
            <label class="label" htmlFor="dataSource">
              {this.#componentLocale.tabBasic.backEnd.dataSource.label}
            </label>
            <ch-combo-box-render
              class="combo-box"
              disabled={this.defaultDataSources.length === 0}
              placeholder={
                this.defaultDataSources.length === 0
                  ? this.#componentLocale.tabBasic.backEnd.dataSource
                      .disabledPlaceholder
                  : ""
              }
              value={this.#selectedDataSourceId}
              ref={(el: HTMLChComboBoxRenderElement) =>
                (this.#dataSourceEl = el as HTMLChComboBoxRenderElement)
              }
              onInput={this.#dataSourceValueChangedHandler}
              model={this.defaultDataSourcesComboBoxModel}
              part="data-source"
              id="dataSource"
            ></ch-combo-box-render>
            {this.#evaluateTooltipRender(this.#dataSourceEl)}
          </div>
        </div>
      </section>,

      <section
        class="section__front-end"
        // front end
      >
        <h2 class="dialog-header dialog-header-with-border dialog-header__front-end subtitle-regular-xs	 text-align-center spacing-body-inline">
          {this.#componentLocale.tabBasic.frontEnd.frontEndLabel}
        </h2>
        <div class="field-group main__front-end spacing-body-inline scrollable">
          {renderChCheckboxItems(
            this.frontEnds,
            this.#frontEndCheckboxInputHandler,
            this.#frontEndsCheckedMap.get(
              this.#selectedPrototypingEnvironmentId
            )
          )}

          {this.noFrontEndSelected && (
            <div class="kb-storage-callout" part="backend-only-callout">
              <ch-image
                class="icon-l"
                src={WARNING_ICON}
                type="mask"
              ></ch-image>

              <div class="kb-storage-callout-inner">
                <span class="kb-storage-callout__title body-semi-bold-s">
                  {
                    this.#componentLocale.tabBasic.frontEnd
                      .backendOnlyCalloutTitle
                  }
                </span>
                <span class="kb-storage-callout__description body-regular-xs">
                  {
                    this.#componentLocale.tabBasic.frontEnd
                      .backendOnlyCalloutDescription
                  }
                </span>
              </div>
            </div>
          )}
        </div>
      </section>
    ];
  };

  // - - - - - - - - - -

  #cancelKbHandler = (): void => {
    if (this.cancelCallback) {
      this.cancelCallback();
    }
  };

  #createKbHandler = (): void => {
    const selectedFrontEndIds =
      this.#frontEndsCheckedMap.get(this.#selectedPrototypingEnvironmentId) ??
      [];

    this.createCallback({
      authenticationType: this.#authenticationTypeEl?.value,
      collation: this.#collationEl?.value,
      createDataFilesInKBFolder: !!this.#createDataFilesInKBFolderEl?.value,
      dataSource: this.#dataSourceEl?.value,
      databaseName: this.#databaseNameEl?.value,
      frontEnds: selectedFrontEndIds,
      kbLocation: this.#locationEl?.value,
      kbName: this.#kbNameEl?.value,
      password: this.#passwordEl?.value,
      prototypingEnvironment: this.#prototypingEnvironmentEl?.value,
      prototypingTarget: this.#prototypingTargetEl?.value,
      savePassword: !!this.#savePasswordEl?.value,
      serverName: this.#serverNameEl?.value,
      userInterfaceLanguage: this.#userInterfaceLanguageEl?.value,
      userName: this.#userNameEl?.value,
      creationMode: this.selectedTab as CreationMode
    }).then((formSubmitResult: FormSubmitResult) => {
      this.validatableControls = validateControls(
        formSubmitResult,
        this.validatableControls
      );
    });
  };

  // TODO: evaluate if this should be a global helper function.
  // It is used on new-kb as well.
  #evaluateTooltipRender = (
    controlReference: HTMLElement
  ): HTMLChTooltipElement =>
    this.validatableControls?.get(controlReference?.id)?.hasError &&
    this.validatableControls.get(controlReference.id)?.message && (
      <ch-tooltip
        class="tooltip"
        actionElement={controlReference as unknown as HTMLButtonElement}
        blockAlign={config.tooltipSettings.blockAlign}
        inlineAlign={config.tooltipSettings.inlineAlign}
        delay={config.tooltipSettings.delay}
      >
        {this.validatableControls.get(controlReference.id).message}
      </ch-tooltip>
    );

  #frontEndCheckboxInputHandler = (
    event: ChCheckboxCustomEvent<string> | InputEvent
  ) => {
    const checkedValue = (event.target as HTMLChCheckboxElement).checkedValue;
    const checked = event.detail === checkedValue;
    this.#updateFrontEndsCheckedMap(checkedValue, checked);
    this.#recomputeBackendOnly();
  };

  /**
   * Recomputes whether the KB will be generated as backend only, i.e. there
   * are front ends to choose from but none is currently selected.
   */
  #recomputeBackendOnly = () => {
    const checkedIds = this.#frontEndsCheckedMap.get(
      this.#selectedPrototypingEnvironmentId
    );
    this.noFrontEndSelected =
      (this.frontEnds?.length ?? 0) > 0 && (checkedIds?.length ?? 0) === 0;
  };

  /**
   * This function updates frontEndsCheckedMap, that contains the checked
   * checkboxes for each language front ends.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #updateFrontEndsCheckedMap = (checkboxId: string, checked: boolean) => {
    let currentState = this.#frontEndsCheckedMap.get(
      this.#selectedPrototypingEnvironmentId
    );
    if (!currentState) {
      // first time
      currentState = [];
      this.#frontEndsCheckedMap.set(
        this.#selectedPrototypingEnvironmentId,
        currentState
      );
    }
    if (checked) {
      // update by adding
      currentState.push(checkboxId);
    } else {
      // update by removing
      const checkboxIndex = currentState.findIndex(id => id === checkboxId);
      removeIndex(currentState, checkboxIndex);
    }
  };

  #getPrototypingEnvironments = async (): Promise<void> => {
    if (this.getPrototypingEnvironmentsCallback) {
      const selectedPrototypingTargetId = this.#prototypingTargetEl
        ? this.#prototypingTargetEl.value
        : this.prototypingTargetsInitialValue;
      const envs = await this.getPrototypingEnvironmentsCallback(
        selectedPrototypingTargetId
      );
      if (envs) {
        this.defaultPrototypingEnvironments = envs;
      }
    }
  };

  #getDataSources = (): void => {
    if (this.getDataSourcesCallback) {
      const selectedPrototypingEnvironmentId =
        this.#prototypingEnvironmentEl.value;
      const selectedPrototypingTargetId = this.#prototypingTargetEl.value;
      const updatedDefaultDataSourcesPromise: Promise<GxOption[]> =
        this.getDataSourcesCallback(
          selectedPrototypingEnvironmentId,
          selectedPrototypingTargetId
        );
      updatedDefaultDataSourcesPromise
        .then(result => {
          if (result.length > 0) {
            result = setSelectedItem(result, this.#selectedDataSourceId);
            this.#selectedDataSourceId = getSelectedItem(result);
            this.defaultDataSources = result;
            // set first option as selected
            this.#dataSourceEl.value =
              this.#selectedDataSourceId !== undefined
                ? this.#selectedDataSourceId
                : this.defaultDataSources[0].id;
          }
        })
        .catch(() => {
          // to do
        });
    }
  };

  #initialSetupAdvancedTab = () => {
    const hasWindowsAuth = this.authenticationTypes.some(
      opt => opt.id === WINDOWS_AUTHENTICATION_VALUE
    );
    if (hasWindowsAuth) {
      this.authenticationTypeValue = WINDOWS_AUTHENTICATION_VALUE;
    }
  };

  #initializeValidatableControlsBasicTab = () => {
    // populate #controlsValidation with the controls that could have errors.
    const validatableControls: HTMLElement[] = [
      this.#dataSourceEl,
      this.#kbNameEl,
      this.#prototypingEnvironmentEl,
      this.#prototypingTargetEl,
      this.#userInterfaceLanguageEl
    ];
    validatableControls.forEach(validatableControl => {
      if (validatableControl.id) {
        this.validatableControls.set(validatableControl.id, {
          reference: validatableControl,
          hasError: false,
          message: undefined
        });
      }
    });
  };

  #initializeValidatableControlsAdvancedTab = () => {
    // populate #controlsValidation with the controls that could have errors.
    const validatableControls: HTMLElement[] = [
      this.#authenticationTypeEl,
      this.#createDataFilesInKBFolderEl,
      this.#serverNameEl,
      this.#authenticationTypeEl,
      this.#databaseNameEl,
      this.#userNameEl,
      this.#collationEl,
      this.#passwordEl,
      this.#savePasswordEl
    ];
    validatableControls.forEach(validatableControl => {
      if (validatableControl.id) {
        this.validatableControls.set(validatableControl.id, {
          reference: validatableControl,
          hasError: false,
          message: undefined
        });
      }
    });
  };

  #selectLocationHandler = async (event: MouseEvent): Promise<void> => {
    // Capture the button before awaiting. Opening/closing the location picker
    // restores focus to it programmatically, leaving the :focus-visible outline
    // lingering as if it were still active. On a successful selection move focus
    // to the location input (natural next step, keyboard-friendly); otherwise
    // just blur the button so the outline doesn't stay pinned.
    const button = event.currentTarget as HTMLButtonElement;
    const kbLocation = await this.selectLocationCallback();
    if (kbLocation) {
      this.kbLocation = kbLocation;
      this.#locationEl?.focus();
    } else {
      button?.blur();
    }
  };

  #tabSelectionChangedHandler = (
    event: CustomEvent<{
      lastSelectedIndex: number;
      newSelectedId: string;
      newSelectedIndex: number;
    }>
  ): void => {
    this.selectedTab = event.detail.newSelectedId;
    if (event.detail.newSelectedId === ADVANCED_TAB_SELECTOR) {
      if (!this.advancedTabIsVisible) {
        this.advancedTabIsVisible = true;
        this.#loadAdvancedTabData();
      }
    } else {
      this.connectionStatus = undefined;
    }
  };

  #loadAdvancedTabData = async (): Promise<void> => {
    if (!this.getAdvancedTabDataCallback || this.loadingAdvancedTab) {
      return;
    }
    this.loadingAdvancedTab = true;
    this.connectionStatus = "connecting";
    try {
      const data = await this.getAdvancedTabDataCallback();
      if (data) {
        if (data.serverName != null) {
          this.serverNameValue = data.serverName;
          this.#applyCreateDataFilesInKBFolderForServer();
        }
        this.serverInstances = data.serverInstances ?? this.serverInstances;
        this.collations = data.collations ?? this.collations;
        this.authenticationTypes =
          data.authenticationTypes ?? this.authenticationTypes;
        this.defaultCollation = data.defaultCollation ?? this.defaultCollation;
        this.authenticationTypeValue =
          data.authenticationTypes?.[0]?.id ?? this.authenticationTypeValue;
        this.connectionStatus = "success";
      } else {
        this.connectionStatus = "failed";
      }
    } catch {
      this.connectionStatus = "failed";
    } finally {
      this.loadingAdvancedTab = false;
    }
  };

  /**
   * Evaluates which front-end options need updates on 'getFrontEndsCallback'
   * by comparing the new state (getFrontEndsCallback result) and previous state.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #updateFrontEnds = async () => {
    const updatedFrontEnds = await this.getFrontEndsCallback(
      this.#selectedPrototypingEnvironmentId
    );
    if (updatedFrontEnds?.length > 0) {
      this.frontEnds = updatedFrontEnds;

      // Seed the checked map from the data's checked property so that
      // initially checked items render as checked on first load.
      if (
        !this.#frontEndsCheckedMap.has(this.#selectedPrototypingEnvironmentId)
      ) {
        const checkedIds = updatedFrontEnds
          .filter(fe => fe.checked)
          .map(fe => fe.value || fe.id);

        if (checkedIds.length > 0) {
          this.#frontEndsCheckedMap.set(
            this.#selectedPrototypingEnvironmentId,
            checkedIds
          );
        }
      }

      this.#recomputeBackendOnly();
    }
  };

  render() {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts
          src={this.#shortcutsSrc}
          ref={(el: HTMLChShortcutsElement) =>
            (this.#chShortcutsEl = el as HTMLChShortcutsElement)
          }
        ></ch-shortcuts>
        <section class="section">
          <header
            class={{
              "field-group": true,
              "dialog-header-with-border": !this.isAdvanced,
              "dialog-header": this.isAdvanced,
              "header": true,
              "header-one-col": true,
              "spacing-body-block-start": true,
              "spacing-body-inline": true
            }}
          >
            <div
              // #kb name
              class="field field-inline kb-name"
            >
              <label class="label kb-label" htmlFor="kbName">
                {this.#componentLocale.header.kbName}
              </label>
              <ch-edit
                // TODO add placeholder
                autoFocus
                class="input kb-input"
                placeholder="SalesInventory"
                value={this.kbName}
                onInput={this.#kbNameInputHandler}
                ref={(el: HTMLChEditElement) =>
                  (this.#kbNameEl = el as HTMLChEditElement)
                }
                id="kbName"
                part="kb-name"
              ></ch-edit>
              {this.#evaluateTooltipRender(this.#kbNameEl)}
            </div>

            {this.canSetLocation && (
              <div class="field field-inline">
                <label class="label" htmlFor="kbLocation">
                  {this.#componentLocale.header.kbLocation}
                </label>
                <div class="location-custom-wrapper">
                  <div class="location-field">
                    <ch-edit
                      class="input"
                      value={this.kbLocation}
                      ref={(el: HTMLChEditElement) =>
                        (this.#locationEl = el as HTMLChEditElement)
                      }
                      id="kbLocation"
                      part="kb-location"
                    ></ch-edit>
                    {this.#evaluateTooltipRender(this.#locationEl)}
                  </div>
                  <button
                    class="button-tertiary button-icon-only "
                    aria-label={
                      this.#componentLocale.header.selectLocationButton
                    }
                    title={this.#componentLocale.header.selectLocationButton}
                    part="button-select-location"
                    onClick={this.#selectLocationHandler}
                    type="button"
                  >
                    <ch-image class="icon-m" src={SYSTEM_ICON}></ch-image>
                  </button>
                </div>
              </div>
            )}
          </header>

          {this.isAdvanced ? (
            <ch-tab-render
              // #tab
              class={{
                scrollable: true,
                tab: true,
                [MAIN_SECTION_CLASSES]: true
              }}
              tabListPosition="block-start"
              model={this.#kbSectionsModel}
              selectedId={BASIC_TAB_SELECTOR}
              overflow="auto"
              contain="size"
              onSelectedItemChange={this.#tabSelectionChangedHandler}
            >
              <div
                slot={BASIC_TAB_SELECTOR}
                class={BASIC_SECTION_PARENT_CLASSES}
                part="tab-button-basic"
              >
                {this.#renderBasic()}
              </div>

              {this.advancedTabIsVisible && (
                <div
                  slot={ADVANCED_TAB_SELECTOR}
                  class="advanced"
                  part="tab-button-advanced"
                >
                  {this.loadingAdvancedTab ? (
                    <gx-ide-loader
                      show
                      class="advanced-loader"
                      loaderTitle={this.#componentLocale.loader.title}
                      description={this.#componentLocale.loader.description}
                    ></gx-ide-loader>
                  ) : (
                    this.#renderAdvanced()
                  )}
                </div>
              )}
            </ch-tab-render>
          ) : (
            <div class={MAIN_SECTION_CLASSES}>
              <div class={BASIC_SECTION_PARENT_CLASSES}>
                {this.#renderBasic()}
              </div>
            </div>
          )}

          <footer
            class={{
              "footer": true,
              "dialog-footer-with-border": true,
              "spacing-body-block-end": true,
              "spacing-body-inline": true
            }}
          >
            {this.connectionStatus && (
              <span
                class={{
                  "body-semi-bold-s": true,
                  "connection-status": true,
                  [`connection-status--${this.connectionStatus}`]: true
                }}
                part={`connection-status connection-status-${this.connectionStatus}`}
              >
                {this.connectionStatus === "connecting" && (
                  <span class="connection-status__spinner"></span>
                )}
                {this.connectionStatus === "success" && (
                  <ch-image
                    class="icon-m"
                    src={SUCCESS_ICON}
                    type="mask"
                  ></ch-image>
                )}
                {this.connectionStatus === "failed" && (
                  <ch-image
                    class="icon-m"
                    src={ERROR_ICON}
                    type="mask"
                  ></ch-image>
                )}
                {
                  this.#componentLocale.footer.connectionStatus[
                    this.connectionStatus
                  ]
                }
              </span>
            )}
            <div class="buttons-spacer">
              <button
                // #cancel
                class="button-secondary"
                id="button-cancel"
                onClick={this.#cancelKbHandler}
                part="button-cancel"
              >
                {this.#componentLocale.footer.btnCancel}
              </button>
              <button
                // #create
                class="button-primary"
                id="button-create"
                disabled={
                  this.connectionStatus === "failed" ||
                  this.connectionStatus === "connecting" ||
                  this.#sqlAuthMissingCredentials()
                }
                onClick={this.#createKbHandler}
                part="button-create"
              >
                {this.#componentLocale.footer.btnCreate}
              </button>
            </div>
          </footer>
        </section>
      </Host>
    );
  }
}
export interface NewKBData {
  authenticationType: string;
  collation: string;
  createDataFilesInKBFolder: boolean;
  databaseName: string;
  dataSource: string;
  frontEnds: string[];
  kbLocation: string;
  kbName: string;
  password: string;
  prototypingEnvironment: string;
  prototypingTarget: string;
  savePassword: boolean;
  serverName: string;
  userInterfaceLanguage: string;
  userName: string;
  creationMode: CreationMode;
}

export type CreationMode = "basic" | "advanced";
export type CreateCallback = (data: NewKBData) => Promise<FormSubmitResult>;

export type CancelCallback = () => Promise<void>;

export type GetDataSourcesCallback = (
  selectedPrototypingEnvironmentId: string,
  selectedPrototypingTargetId: string
) => Promise<GxOption[]>;

export type GetFrontEndsCallback = (
  selectedPrototypingEnvironmentId: string
) => Promise<GxOption[]>;

export type GetPrototypingEnvironmentsCallback = (
  selectedPrototypingTargetId: string
) => Promise<GxOption[]>;

export type SelectLocationCallback = () => Promise<string>;

export type BrowseForMoreCallback = () => Promise<string>;

export type CollationsForInstanceResult = {
  collations: GxOption[];
  defaultCollation: string;
};

export type GetCollationsForInstanceCallback = (
  serverInstance: string,
  authType?: string,
  userName?: string,
  password?: string
) => Promise<CollationsForInstanceResult | undefined>;

export interface AdvancedTabData {
  serverName?: string;
  serverInstances?: GxOption[];
  collations?: GxOption[];
  authenticationTypes?: GxOption[];
  defaultCollation?: string;
}

export type GetAdvancedTabDataCallback = () => Promise<
  AdvancedTabData | undefined
>;
